import { useEffect, useMemo, useState } from "react";
import "./Dashboard.css";

const RANGE_OPTIONS = [7, 30, 60, 90];

function formatNumber(value) {
  return new Intl.NumberFormat("nl-BE").format(Number(value || 0));
}

function formatDate(value) {
  return new Intl.DateTimeFormat("nl-BE", {
    day: "2-digit",
    month: "short",
  }).format(new Date(value));
}

function formatDateTime(value) {
  return new Intl.DateTimeFormat("nl-BE", {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date(value));
}

function MetricCard({ label, value, helper }) {
  return (
    <div className="dashboard-card metric-card">
      <p className="metric-label">{label}</p>
      <strong className="metric-value">{formatNumber(value)}</strong>
      <span className="metric-helper">{helper}</span>
    </div>
  );
}

export default function Dashboard() {
  const [rangeDays, setRangeDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;

    async function loadDashboard() {
      setLoading(true);
      setError("");

      try {
        const response = await fetch(`/api/dashboard/summary?days=${rangeDays}`);
        if (!response.ok) {
          throw new Error(`dashboard request failed with ${response.status}`);
        }

        const payload = await response.json();
        if (active) {
          setData(payload);
        }
      } catch (err) {
        if (active) {
          setError("Dashboardgegevens zijn momenteel niet beschikbaar.");
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    }

    loadDashboard();
    return () => {
      active = false;
    };
  }, [rangeDays]);

  const maxDailyViews = useMemo(() => {
    if (!data?.daily?.length) return 1;
    return Math.max(...data.daily.map((entry) => Number(entry.views || 0)), 1);
  }, [data]);

  return (
    <main className="dashboard-page">
      <div className="dashboard-shell">
        <header className="dashboard-header">
          <div>
            <p className="dashboard-eyebrow">Ckris analytics</p>
            <h1>Website dashboard</h1>
            <p className="dashboard-subtitle">
              Overzicht van bezoekers, populairste pagina&apos;s en recente activiteit.
            </p>
          </div>

          <div className="dashboard-actions">
            <a className="dashboard-link" href="/">
              Terug naar website
            </a>
          </div>
        </header>

        <section className="dashboard-toolbar" aria-label="Periode filter">
          {RANGE_OPTIONS.map((option) => (
            <button
              key={option}
              type="button"
              className={`range-button ${rangeDays === option ? "is-active" : ""}`}
              onClick={() => setRangeDays(option)}
            >
              Laatste {option} dagen
            </button>
          ))}
        </section>

        {loading && <div className="dashboard-state">Dashboard wordt geladen...</div>}
        {error && !loading && <div className="dashboard-state dashboard-error">{error}</div>}

        {!loading && !error && data && (
          <>
            <section className="dashboard-grid metrics-grid">
              <MetricCard
                label="Bezoeken"
                value={data.totals.range_views}
                helper={`Laatste ${data.rangeDays} dagen`}
              />
              <MetricCard
                label="Vandaag"
                value={data.totals.today_views}
                helper="Sinds middernacht"
              />
              <MetricCard
                label="Laatste 7 dagen"
                value={data.totals.last_7_days_views}
                helper="Recente trend"
              />
              <MetricCard
                label="Unieke bezoekers"
                value={data.totals.unique_visitors}
                helper={`Op basis van IP in ${data.rangeDays} dagen`}
              />
              <MetricCard
                label="Unieke pagina's"
                value={data.totals.unique_pages}
                helper="Over alle gemeten events"
              />
              <MetricCard
                label="Totaal gemeten"
                value={data.totals.total_views}
                helper="Sinds start tracking"
              />
            </section>

            <section className="dashboard-grid charts-grid">
              <article className="dashboard-card">
                <div className="dashboard-card-header">
                  <h2>Dagelijkse bezoeken</h2>
                  <span>{data.rangeDays} dagen</span>
                </div>
                <div className="chart-bars" role="img" aria-label="Dagelijkse bezoekgrafiek">
                  {data.daily.map((entry) => (
                    <div key={entry.date} className="chart-bar-group">
                      <div
                        className="chart-bar"
                        style={{ height: `${Math.max((Number(entry.views || 0) / maxDailyViews) * 100, 6)}%` }}
                        title={`${entry.date}: ${entry.views} bezoeken`}
                      />
                      <span>{formatDate(entry.date)}</span>
                    </div>
                  ))}
                </div>
              </article>

              <article className="dashboard-card">
                <div className="dashboard-card-header">
                  <h2>Populaire pagina&apos;s</h2>
                  <span>Top 8</span>
                </div>
                <div className="table-list">
                  {data.topPages.map((row) => (
                    <div key={row.page} className="table-row">
                      <div className="table-primary">{row.page}</div>
                      <strong>{formatNumber(row.views)}</strong>
                    </div>
                  ))}
                </div>
              </article>

              <article className="dashboard-card">
                <div className="dashboard-card-header">
                  <h2>Verwijzers</h2>
                  <span>Top 8</span>
                </div>
                <div className="table-list">
                  {data.topReferrers.map((row) => (
                    <div key={row.referrer} className="table-row">
                      <div className="table-primary">{row.referrer}</div>
                      <strong>{formatNumber(row.visits)}</strong>
                    </div>
                  ))}
                </div>
              </article>
            </section>

            <section className="dashboard-card">
              <div className="dashboard-card-header">
                <h2>Recente events</h2>
                <span>Laatste 20</span>
              </div>
              <div className="events-table-wrapper">
                <table className="events-table">
                  <thead>
                    <tr>
                      <th>Tijdstip</th>
                      <th>Event</th>
                      <th>Pagina</th>
                      <th>Referrer</th>
                      <th>Breedte</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.recent.map((row) => (
                      <tr key={row.id}>
                        <td>{formatDateTime(row.created_at)}</td>
                        <td>{row.event}</td>
                        <td>{row.page}</td>
                        <td>{row.referrer}</td>
                        <td>{row.screen_width || "-"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          </>
        )}
      </div>
    </main>
  );
}
